import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Overlay from '../overlay';
import zhCN from '../locale/zh-cn';
import { focus, obj, events, dom } from '../util';
import Inner from './inner';

const noop = () => {};
const { limitTabRange } = focus;
const { pickOthers } = obj;
const { getStyle, setStyle } = dom;
let mousePosition = {};

const getScroll = (w, top) => {
  let ret = w[`page${top ? 'Y' : 'X'}Offset`];
  const method = `scroll${top ? 'Top' : 'Left'}`;
  if (typeof ret !== 'number') {
    const d = w.document;
    ret = d.documentElement[method];
    if (typeof ret !== 'number') {
      ret = d.body[method];
    }
  }
  return ret;
};

const setTransformOrigin = (node, value) => {
  const { style } = node;
  ['Webkit', 'Moz', 'Ms', 'ms'].forEach(prefix => {
    style[`${prefix}TransformOrigin`] = value;
  });
  style.transformOrigin = value;
};

const offset = el => {
  const rect = el.getBoundingClientRect();
  const pos = {
    left: rect.left,
    top: rect.top,
  };
  const doc = el.ownerDocument;
  const w = doc.defaultView || doc.parentWindow;
  pos.left += getScroll(w);
  pos.top += getScroll(w, true);
  return pos;
};

const getClickPosition = e => {
  mousePosition = {
    x: e.pageX,
    y: e.pageY,
  };
  // 100ms 内发生过点击事件，则从点击位置动画展示
  // 否则直接 zoom 展示
  // 这样可以兼容非点击方式展开
  setTimeout(() => {
    mousePosition = null;
  }, 100);
};

// 只有点击事件支持从鼠标位置动画展开
if (typeof window !== 'undefined' && window.document && window.document.documentElement) {
  document.documentElement.addEventListener('click', getClickPosition);
}
/**
 * Dialog
 */
export default class Dialog extends Component {
  static propTypes = {
    prefix: PropTypes.string,
    pure: PropTypes.bool,
    rtl: PropTypes.bool,
    className: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium']),
    /**
     * 是否显示
     */
    visible: PropTypes.bool,
    /**
     * 标题
     */
    title: PropTypes.node,
    /**
     * 内容
     */
    children: PropTypes.node,
    /**
     * 底部内容，设置为 false，则不进行显示
     * @default [<Button type="primary">确定</Button>, <Button>取消</Button>]
     */
    footer: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
    /**
     * 底部按钮的对齐方式
     */
    footerAlign: PropTypes.oneOf(['left', 'center', 'right']),
    /**
     * 指定确定按钮和取消按钮是否存在以及如何排列,<br><br>**可选值**：
     * ['ok', 'cancel']（确认取消按钮同时存在，确认按钮在左）
     * ['cancel', 'ok']（确认取消按钮同时存在，确认按钮在右）
     * ['ok']（只存在确认按钮）
     * ['cancel']（只存在取消按钮）
     */
    footerActions: PropTypes.array,
    /**
     * 在点击确定按钮时触发的回调函数
     * @param {Object} event 点击事件对象
     */
    onOk: PropTypes.func,
    /**
     * 在点击取消按钮时触发的回调函数
     * @param {Object} event 点击事件对象
     */
    onCancel: PropTypes.func,
    /**
     * 应用于确定按钮的属性对象
     */
    okProps: PropTypes.object,
    /**
     * 应用于取消按钮的属性对象
     */
    cancelProps: PropTypes.object,
    /**
     * 控制对话框关闭的方式，值可以为字符串或者布尔值，其中字符串是由以下值组成：
     * **close** 表示点击关闭按钮可以关闭对话框
     * **mask** 表示点击遮罩区域可以关闭对话框
     * **esc** 表示按下 esc 键可以关闭对话框
     * 如 'close' 或 'close,esc,mask'
     * 如果设置为 true，则以上关闭方式全部生效
     * 如果设置为 false，则以上关闭方式全部失效
     */
    closeable: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    /**
     * 对话框关闭时触发的回调函数
     * @param {String} trigger 关闭触发行为的描述字符串
     * @param {Object} event 关闭时事件对象
     */
    onClose: PropTypes.func,
    /**
     * 对话框关闭后触发的回调函数, 如果有动画，则在动画结束后触发
     */
    afterClose: PropTypes.func,
    /**
     * 是否显示遮罩
     */
    hasMask: PropTypes.bool,
    /**
     * 显示隐藏时动画的播放方式
     * @property {String} in 进场动画
     * @property {String} out 出场动画
     */
    animation: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    /**
     * 对话框弹出时是否自动获得焦点
     */
    autoFocus: PropTypes.bool,
    /**
     * 对话框距离浏览器顶部和底部的最小间距
     */
    minMargin: PropTypes.number,
    /**
     * 透传到弹层组件的属性对象
     */
    overlayProps: PropTypes.object,
    /**
     * 自定义国际化文案对象
     * @property {String} ok 确认按钮文案
     * @property {String} cancel 取消按钮文案
     */
    locale: PropTypes.object,
    /**
     * 对话框的高度样式属性
     */
    height: PropTypes.string,
    /**
     * 没有内容区的padding
     */
    noBodyPadding: PropTypes.bool,
    /**
     * 是否处于加载状态
     */
    loading: PropTypes.bool,
    /**
     * 加载提示
     */
    loadingTip: PropTypes.node,
  };

  static defaultProps = {
    prefix: 'next-',
    pure: false,
    size: 'medium',
    visible: false,
    footerAlign: 'right',
    footerActions: ['cancel', 'ok'],
    onOk: noop,
    onCancel: noop,
    okProps: {},
    cancelProps: {},
    closeable: 'esc,close,mask',
    onClose: noop,
    afterClose: noop,
    hasMask: true,
    animation: {
      in: 'zoomIn',
      out: 'zoomOut',
    },
    autoFocus: false,
    minMargin: 56,
    overlayProps: {},
    locale: zhCN.Dialog,
    noBodyPadding: false,
    loading: false,
  };

  constructor(props) {
    super(props);
    this.resizeTimeout = null;
    this.lastBodyHeight = 0;
  }

  componentDidMount() {
    events.on(document, 'keydown', this.onKeyDown);
    events.on(window, 'resize', this.handleResize);
    setTimeout(() => {
      this.adjustPosition();
      this.lastBodyHeight = this.getBodyHeight();
    });
  }

  componentWillReceiveProps(nextProps) {
    // 显式置零this.lastBodyHeight，否则如果显式设置bodyHeight（通过height属性或者样式）
    // 那么在重新显式时，会因为componentDidUpdate中定位触发条件不满足而不进行定位调整
    if (!nextProps.visible) {
      this.lastBodyHeight = 0;
    }
    // if (nextProps.visible) {
    //   setTimeout(() => {
    //     const node = this.getInnerNode();
    //     const elOffset = offset(node);
    //     setTransformOrigin(node, `${mousePosition.x - elOffset.left}px ${mousePosition.y - elOffset.top}px`);
    //   });
    // }
  }

  componentDidUpdate(preProps) {
    // 如何让可滚动内容滚动到顶部：滚动一段距离，设置overflow使其不滚动，正常展开，再设置可滚动(下面adjustPosition逻辑)，即可滚动到顶部（双刃剑）
    // 加层判断，防止重新渲染时滚到页面顶部
    const bodyHeight = this.getBodyHeight();
    if (
      this.props.visible &&
      (preProps.minMargin !== this.props.minMargin || bodyHeight !== this.lastBodyHeight)
    ) {
      setTimeout(() => {
        this.adjustPosition();
        const node = this.getInnerNode();
        const elOffset = offset(node);
        setTransformOrigin(
          node,
          `${mousePosition.x - elOffset.left}px ${mousePosition.y - elOffset.top}px`,
        );
        this.lastBodyHeight = this.getBodyHeight();
      });
    }
  }

  componentWillUnmount() {
    events.off(document, 'keydown', this.onKeyDown);
    events.off(window, 'resize', this.handleResize);

    clearTimeout(this.resizeTimeout);
  }

  getBodyMinHeight = () => {
    const { size } = this.props;
    if (size === 'small') {
      return '168px';
    }
    return '224px';
  };

  getBodyHeight = () => {
    const { visible } = this.props;
    const inner = this.getInner();
    if (visible && inner) {
      const { bodyNode } = inner;
      const bodyHeight = bodyNode ? bodyNode.offsetHeight : 0;
      return bodyHeight;
    }
    return 0;
  };

  handleResize = () => {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.adjustPosition();
    }, 200);
  };

  onKeyDown = e => {
    const node = this.getInnerNode();
    if (node) {
      limitTabRange(node, e);
    }
  };

  adjustPosition = () => {
    const { minMargin, visible, className, prefix } = this.props;
    const inner = this.getInner();
    const isQuick = className && className.includes(`${prefix}dialog-quick`);
    if (visible && inner) {
      const innerNode = this.getInnerNode();
      const { headerNode, bodyNode, footerNode } = inner;
      // 先去除掉高度限制
      bodyNode &&
        setStyle(bodyNode, {
          'min-height': 'none',
          'max-height': 'none',
        });
      setStyle(innerNode, {
        left: `calc(50% - ${getStyle(innerNode, 'width') / 2}px)`,
        top: `${minMargin}px`,
      });
      // 快捷调用不适用该规则
      if (!isQuick) {
        bodyNode &&
          setStyle(bodyNode, {
            'min-height': this.getBodyMinHeight(),
          });
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const headerHeight = headerNode ? getStyle(headerNode, 'height') : 0;
        const bodyHeight = bodyNode ? getStyle(bodyNode, 'height') : 0;
        const footerHeight = footerNode ? getStyle(footerNode, 'height') : 0;
        if (viewportHeight - headerHeight - bodyHeight - footerHeight < 2 * minMargin) {
          bodyNode &&
            setStyle(bodyNode, {
              'max-height': `${viewportHeight - headerHeight - 2 * minMargin - footerHeight}px`,
              'overflow-y': 'auto',
            });
        }
      }
    }
  };

  mapcloseableToConfig = closeable => {
    return ['esc', 'close', 'mask'].reduce((ret, option) => {
      const key = option.charAt(0).toUpperCase() + option.substr(1);
      const value =
        typeof closeable === 'boolean' ? closeable : closeable.split(',').indexOf(option) > -1;

      if (option === 'esc' || option === 'mask') {
        ret[`canCloseBy${key}`] = value;
      } else {
        ret[`canCloseBy${key}Click`] = value;
      }

      return ret;
    }, {});
  };

  getOverlayRef = ref => {
    this.overlay = ref;
  };

  getInner = () => {
    return this.overlay.getInstance().getContent();
  };

  getInnerNode = () => {
    return this.overlay.getInstance().getContentNode();
  };

  renderInner = closeable => {
    const {
      prefix,
      className,
      size,
      title,
      children,
      footer,
      footerAlign,
      footerActions,
      onOk,
      onCancel,
      okProps,
      cancelProps,
      onClose,
      locale,
      visible,
      rtl,
      height,
      noBodyPadding,
      loading,
      loadingTip,
    } = this.props;
    const others = pickOthers(Object.keys(Dialog.propTypes), this.props);

    return (
      <Inner
        prefix={prefix}
        className={className}
        size={size}
        title={title}
        footer={footer}
        footerAlign={footerAlign}
        footerActions={footerActions}
        onOk={visible ? onOk : noop}
        onCancel={visible ? onCancel : noop}
        okProps={okProps}
        cancelProps={cancelProps}
        locale={locale}
        closeable={closeable}
        rtl={rtl}
        onClose={onClose.bind(this, 'closeClick')}
        height={height}
        noBodyPadding={noBodyPadding}
        loading={loading}
        loadingTip={loadingTip}
        {...others}
      >
        {children}
      </Inner>
    );
  };

  render() {
    const {
      prefix,
      visible,
      hasMask,
      animation,
      autoFocus,
      closeable,
      onClose,
      afterClose,
      overlayProps,
      rtl,
    } = this.props;

    const { canCloseByCloseClick, ...closeConfig } = this.mapcloseableToConfig(closeable);
    const newOverlayProps = {
      ...overlayProps,
      prefix,
      visible,
      animation,
      hasMask,
      autoFocus,
      afterClose,
      ...closeConfig,
      align: false,
      canCloseByOutSideClick: false,
      onRequestClose: onClose,
      needAdjust: false,
      needListenResize: false,
      disableScroll: true,
      ref: this.getOverlayRef,
      rtl,
    };

    const inner = this.renderInner(canCloseByCloseClick);
    // setTransformOrigin(inner, `${mousePosition.x}px ${mousePosition.y}px`);
    return <Overlay {...newOverlayProps}>{inner}</Overlay>;
  }
}
